import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPage,
  EuiPageBody,
  EuiPanel,
  EuiProgress,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useSearchParams } from "react-router-dom";
import { useAnketaI18n } from "src/internationalization";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { OpsanioApplianceData } from "../appliance";
import { relativeLabel } from "../appliance/format";
import { HealthStatus } from "../appliance/health";
import { waveColor } from "../appliance/waves";
import { FleetHealthEntry, useFleetHealth } from "../appliance/useFleetHealth";
import {
  COMPACT_INCIDENT_CARD_HEIGHT,
  CountPanel,
  FleetTile,
  INCIDENT_CARD_HEIGHT,
  IncidentCard,
} from "./cards";
import {
  buildWallModel,
  fleetColumns,
  diffStatuses,
  NO_CHANGES,
  parseWallSettings,
  StatusMap,
  statusMap,
  WallChanges,
  WALL_LIST_REFRESH_MS,
  WALL_RETRY_SECONDS,
} from "./wall";

const i18n = "anketa.opsanio.health";

/**
 * OUI's EuiFlexGroup (and EuiPanel) carry `flex-grow: 1`. Inside the wall's
 * full-height flex columns that turns every header row into a spacer, so rows
 * that should hug their content opt out.
 */
const noGrow: React.CSSProperties = { flexGrow: 0 };

/** More incidents than this use the smaller minimum card height. */
const COMPACT_ABOVE = 6;

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const mmss = (sec: number) => `${Math.floor(sec / 60)}:${pad(sec % 60)}`;
const timeOfDay = (ms: number) =>
  new Date(ms).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

/**
 * opsanio › Health: a self-refreshing wall view of the fleet's live PRTG
 * health. Problem appliances get a card with their alerting sensors, the rest
 * a compact tile. `?kiosk=1` drops the app chrome for a wall screen.
 *
 * Reads the same two endpoints as the appliance page (`/appliances/summary`,
 * `/prtg/instance/:name`), both gated server-side on `opsanio/appliance · view`
 * and the caller's fleet scope - the route gate here only hides the page.
 */
export const OpsanioHealthWall = (): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const settings = useMemo(
    () => parseWallSettings(searchParams),
    [searchParams],
  );

  // ---- Appliance list (rarely changes; reloaded every 15 min) ----
  const [appliances, setAppliances] = useState<OpsanioApplianceData[] | null>(
    null,
  );
  const [listError, setListError] = useState<string | null>(null);
  // `Get` gets a new identity on every render; read it through a ref so the
  // reload timer isn't torn down and re-armed each second.
  const getRef = useRef(Get);
  getRef.current = Get;
  const canFetch = typeof keycloak?.token === "string";

  const loadList = async () => {
    if (typeof keycloak?.token !== "string") return;
    const { success, data, error } = await getRef.current({
      path: `v2/${AnketaResourcesEnum.opsanio}/appliances/summary`,
      responseType: "json",
      token: keycloak.token,
    });
    if (!success || error || !Array.isArray(data)) {
      // Keep the list we have; only an empty wall shows the error.
      setListError(error?.message ?? "");
      return;
    }
    setListError(null);
    setAppliances(data as OpsanioApplianceData[]);
  };

  useEffect(() => {
    if (!canFetch) return;
    loadList();
    const timer = window.setInterval(loadList, WALL_LIST_REFRESH_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]);

  const scoped = useMemo(
    () =>
      (appliances ?? []).filter(
        (a) =>
          !a.archived &&
          (settings.waves === null || settings.waves.includes(a.wave)),
      ),
    [appliances, settings.waves],
  );

  const health = useFleetHealth(scoped, 4, {
    engagement: false,
    keepOnOutage: true,
  });

  // ---- Refresh schedule ----
  const [now, setNow] = useState(() => Date.now());
  const [paused, setPaused] = useState(false);
  const nextRunAt = useRef(0);
  const runRef = useRef(health.run);
  runRef.current = health.run;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // The next sweep is due one interval after the last one finished (or the
  // retry cadence while PRTG is down), so slow sweeps never overlap.
  useEffect(() => {
    if (health.lastRunAt === null) return;
    nextRunAt.current =
      health.lastRunAt +
      (health.outage ? WALL_RETRY_SECONDS : settings.interval) * 1000;
  }, [health.lastRunAt, health.outage, settings.interval]);

  // A different wave selection is a different fleet: sweep it right away.
  const scopeKey = scoped.map((a) => a.id).join(",");
  useEffect(() => {
    nextRunAt.current = 0;
  }, [scopeKey]);

  useEffect(() => {
    if (paused || health.loading || scoped.length === 0) return;
    // A hidden tab doesn't sweep; it catches up on the first tick when shown.
    if (document.visibilityState !== "visible") return;
    if (now < nextRunAt.current) return;
    // Provisional; replaced by the effect above when the sweep finishes.
    nextRunAt.current = now + settings.interval * 1000;
    runRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, paused, health.loading, scoped.length]);

  // ---- Model + change markers between the last two sweeps ----
  const entries = useMemo<FleetHealthEntry[]>(
    () =>
      scoped
        .map((a) => health.byId[a.id])
        .filter((e): e is FleetHealthEntry => Boolean(e)),
    [scoped, health.byId],
  );
  const model = useMemo(() => buildWallModel(entries), [entries]);

  const previous = useRef<StatusMap | null>(null);
  const [changes, setChanges] = useState<WallChanges>(NO_CHANGES);
  useEffect(() => {
    if (!health.loaded || entries.length === 0) return;
    const current = statusMap(entries);
    setChanges(diffStatuses(previous.current, current));
    previous.current = current;
    // Only a new snapshot counts as a refresh (an outage keeps the old one).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [health.byId]);

  const newIds = useMemo(() => new Set(changes.newIds), [changes]);
  const recoveredIds = useMemo(() => new Set(changes.recoveredIds), [changes]);
  const recoveredAt =
    health.lastSuccessAt !== null
      ? timeOfDay(health.lastSuccessAt).slice(0, 5)
      : null;
  const recoveredNames = model.tiles
    .filter((e) => recoveredIds.has(e.appliance.id))
    .map((e) => e.appliance.displayName || e.appliance.id);

  // ---- Kiosk ----
  const toggleKiosk = () => {
    const next = new URLSearchParams(searchParams);
    if (settings.kiosk) {
      next.delete("kiosk");
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => undefined);
      }
    } else {
      next.set("kiosk", "1");
      // Needs the click's user gesture; a refused request just stays windowed.
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    }
    setSearchParams(next);
  };

  // ---- Labels ----
  const statusLabel: Record<HealthStatus, string> = {
    critical: translate(`${i18n}.status.critical`, { defaultMessage: "Critical" }),
    warning: translate(`${i18n}.status.warning`, { defaultMessage: "Warning" }),
    healthy: translate(`${i18n}.status.healthy`, { defaultMessage: "Healthy" }),
    unknown: translate(`${i18n}.status.unknown`, {
      defaultMessage: "Unmonitored",
    }),
  };
  const statusDescription: Record<HealthStatus, string> = {
    critical: translate(`${i18n}.count.critical`, {
      defaultMessage: "a sensor is down",
    }),
    warning: translate(`${i18n}.count.warning`, {
      defaultMessage: "warning or unusual sensor",
    }),
    healthy: translate(`${i18n}.count.healthy`, {
      defaultMessage: "all sensors up",
    }),
    unknown: translate(`${i18n}.count.unknown`, {
      defaultMessage: "no sensors or PRTG unreachable",
    }),
  };

  const wavesLabel =
    settings.waves === null
      ? translate(`${i18n}.waves.all`, { defaultMessage: "all waves" })
      : translate(`${i18n}.waves.some`, {
          values: { waves: settings.waves.join(", ") },
          defaultMessage: "waves {{waves}}",
        });
  const subtitle = translate(`${i18n}.subtitle`, {
    values: {
      total: scoped.length,
      waves: wavesLabel,
      sync: relativeLabel(
        health.lastSuccessAt !== null
          ? new Date(health.lastSuccessAt).toISOString()
          : null,
        translate,
      ),
    },
    defaultMessage: "{{total}} appliances · {{waves}} · health sync {{sync}}",
  });

  const cycle = (health.outage ? WALL_RETRY_SECONDS : settings.interval) * 1000;
  const remaining = Math.max(
    0,
    Math.ceil((Math.min(nextRunAt.current, now + cycle) - now) / 1000),
  );
  let refreshLabel: string;
  if (paused) {
    refreshLabel = translate(`${i18n}.refresh.paused`, {
      defaultMessage: "Auto-refresh paused",
    });
  } else if (health.loading) {
    refreshLabel = translate(`${i18n}.refresh.running`, {
      values: { done: health.progress.done, total: health.progress.total },
      defaultMessage: "Refreshing… {{done}}/{{total}}",
    });
  } else if (health.outage) {
    refreshLabel = translate(`${i18n}.refresh.retry`, {
      values: { time: mmss(remaining) },
      defaultMessage: "Retrying in {{time}}",
    });
  } else {
    refreshLabel = translate(`${i18n}.refresh.next`, {
      values: { time: mmss(remaining) },
      defaultMessage: "Next refresh in {{time}}",
    });
  }

  const stale = health.outage !== null;
  const hasData = health.lastSuccessAt !== null;
  const compact = model.incidents.length > COMPACT_ABOVE;
  const allClear =
    health.loaded &&
    model.total > 0 &&
    model.counts.healthy === model.total;
  const kiosk = settings.kiosk;

  // ---- Layout ----
  const sectionTitle = (title: string, count: number, hint?: string) => (
    <EuiFlexGroup
      gutterSize="s"
      alignItems="center"
      responsive={false}
      style={noGrow}
    >
      <EuiFlexItem grow={false}>
        <EuiTitle size="xs">
          <h2>{title}</h2>
        </EuiTitle>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiText size="s" color="subdued">
          {count}
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem />
      {hint && (
        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            {hint}
          </EuiText>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );

  // On the wall the lists scroll inside the screen. Reserve room for the
  // scrollbar so it never sits on top of the sensor messages (overlay
  // scrollbars take no gutter, hence the padding too).
  const scrollArea: React.CSSProperties = kiosk
    ? { overflowY: "auto", scrollbarGutter: "stable", paddingRight: 8 }
    : {};

  // One column per wave; the wave badge heads the column, not every tile.
  const columns = fleetColumns(model.tiles, settings.waves);
  const fleetGrid = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.max(1, columns.length)}, minmax(0, 1fr))`,
        gap: 8,
        alignContent: "start",
        flex: 1,
        minHeight: 0,
        ...scrollArea,
      }}
    >
      {columns.map((col) => (
        <div
          key={col.wave || "-"}
          style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}
        >
          <EuiFlexGroup
            gutterSize="xs"
            alignItems="center"
            responsive={false}
            style={noGrow}
          >
            <EuiFlexItem grow={false}>
              <EuiBadge color={waveColor(col.wave)}>{col.wave || "-"}</EuiBadge>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {col.entries.length}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          {col.entries.map((e) => (
            <FleetTile
              key={e.appliance.id}
              entry={e}
              showWave={false}
              recoveredAt={recoveredIds.has(e.appliance.id) ? recoveredAt : null}
            />
          ))}
        </div>
      ))}
    </div>
  );

  const column: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
    minHeight: 0,
  };

  let body: JSX.Element;
  if (appliances === null) {
    body = listError !== null ? (
      <EuiCallOut
        color="danger"
        iconType="alert"
        title={translate(`${i18n}.listError`, {
          defaultMessage: "Could not load the appliances",
        })}
      >
        {listError && <p>{listError}</p>}
        <EuiButton color="danger" size="s" iconType="refresh" onClick={loadList}>
          {translate("anketa.common.retry", { defaultMessage: "Retry" })}
        </EuiButton>
      </EuiCallOut>
    ) : (
      <EuiProgress size="xs" color="primary" />
    );
  } else if (scoped.length === 0) {
    body = (
      <EuiEmptyPrompt
        iconType="visPulse"
        title={
          <h2>
            {translate(`${i18n}.empty.title`, {
              defaultMessage: "No appliances in these waves",
            })}
          </h2>
        }
        body={
          <p>
            {translate(`${i18n}.empty.body`, {
              defaultMessage:
                "Pick other waves with ?waves=main,wave1 or ?waves=all in the address.",
            })}
          </p>
        }
      />
    );
  } else if (!health.loaded) {
    body = (
      <div>
        <EuiProgress
          value={health.progress.done}
          max={health.progress.total || 1}
          size="xs"
          color="primary"
        />
        <EuiText size="xs" color="subdued">
          {translate("anketa.opsanio.overview.loading", {
            values: {
              done: health.progress.done,
              total: health.progress.total,
            },
            defaultMessage: "Loading live health… {{done}}/{{total}}",
          })}
        </EuiText>
      </div>
    );
  } else if (allClear) {
    body = (
      <div style={{ ...column, gap: 16, flex: 1, opacity: stale ? 0.55 : 1 }}>
        <EuiPanel hasBorder color="success" paddingSize="l" grow={false}>
          <EuiFlexGroup gutterSize="l" alignItems="center" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type="checkInCircleFilled" color="success" size="xxl" />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiTitle size="l">
                <h2>
                  {translate(`${i18n}.allClear.title`, {
                    values: { total: model.total },
                    defaultMessage: "All {{total}} appliances healthy",
                  })}
                </h2>
              </EuiTitle>
              <EuiText size="s">
                {translate(`${i18n}.allClear.body`, {
                  values: {
                    time:
                      health.lastSuccessAt !== null
                        ? timeOfDay(health.lastSuccessAt)
                        : "-",
                  },
                  defaultMessage:
                    "No sensor down, in warning or unusual at the refresh of {{time}}.",
                })}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
        {sectionTitle(
          translate(`${i18n}.fleet`, { defaultMessage: "Fleet" }),
          model.tiles.length,
        )}
        {fleetGrid}
      </div>
    );
  } else {
    body = (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))",
          gap: 16,
          flex: 1,
          minHeight: 0,
          opacity: stale ? 0.55 : 1,
        }}
      >
        <section style={column}>
          {sectionTitle(
            translate(`${i18n}.attention`, { defaultMessage: "Need attention" }),
            model.incidents.length,
            translate(`${i18n}.attention.order`, {
              defaultMessage: "critical first, then lowest score",
            }),
          )}
          {model.incidents.length === 0 ? (
            <EuiPanel hasBorder color="subdued" paddingSize="m" grow={false}>
              <EuiText size="s" color="subdued">
                {translate(`${i18n}.attention.none`, {
                  defaultMessage: "No appliance is critical or in warning.",
                })}
              </EuiText>
            </EuiPanel>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
                // Rows share the free height when there are few incidents and
                // keep their minimum (then scroll) when there are many.
                gridAutoRows: `minmax(${
                  compact ? COMPACT_INCIDENT_CARD_HEIGHT : INCIDENT_CARD_HEIGHT
                }px, 1fr)`,
                gap: 16,
                flex: 1,
                minHeight: 0,
                ...scrollArea,
              }}
            >
              {model.incidents.map((e) => (
                <IncidentCard
                  key={e.appliance.id}
                  entry={e}
                  isNew={newIds.has(e.appliance.id)}
                />
              ))}
            </div>
          )}
        </section>
        <section style={column}>
          {sectionTitle(
            translate(`${i18n}.fleet`, { defaultMessage: "Fleet" }),
            model.tiles.length,
            translate(`${i18n}.fleet.order`, {
              defaultMessage: "one column per wave, A–Z",
            }),
          )}
          {fleetGrid}
        </section>
      </div>
    );
  }

  const content = (
    <div
      style={
        kiosk
          ? {
              zoom: settings.zoom,
              // 100vh is scaled by zoom too; divide it back out.
              height: `${100 / settings.zoom}vh`,
              boxSizing: "border-box",
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              overflow: "hidden",
            }
          : { display: "flex", flexDirection: "column", gap: 16 }
      }
    >
      {/* ---- Header: title, clock + countdown, actions ---- */}
      <EuiFlexGroup
        alignItems="flexStart"
        gutterSize="m"
        wrap
        responsive={false}
        style={noGrow}
      >
        <EuiFlexItem style={{ minWidth: 320 }}>
          <EuiTitle size="l">
            <h1>{translate(`${i18n}.title`, { defaultMessage: "Health" })}</h1>
          </EuiTitle>
          <EuiText size="s" color="subdued">
            {subtitle}
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false} style={{ alignItems: "flex-end", minWidth: 180 }}>
          <EuiTitle size={kiosk ? "l" : "m"}>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {timeOfDay(now)}
            </span>
          </EuiTitle>
          <EuiText
            size="xs"
            color={stale ? "warning" : "subdued"}
            style={{ fontVariantNumeric: "tabular-nums" }}
            aria-live="off"
          >
            {refreshLabel}
          </EuiText>
          <div style={{ width: 180 }}>
            <EuiProgress
              value={paused || health.loading ? 0 : remaining}
              max={cycle / 1000}
              size="xs"
              color={stale ? "warning" : "primary"}
              aria-hidden="true"
            />
          </div>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            iconType={paused ? "play" : "pause"}
            onClick={() => setPaused((p) => !p)}
          >
            {paused
              ? translate(`${i18n}.resume`, { defaultMessage: "Resume" })
              : translate(`${i18n}.pause`, { defaultMessage: "Pause" })}
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton fill iconType="fullScreen" onClick={toggleKiosk}>
            {kiosk
              ? translate(`${i18n}.kiosk.exit`, { defaultMessage: "Exit kiosk" })
              : translate(`${i18n}.kiosk.enter`, { defaultMessage: "Kiosk mode" })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      {stale && (
        <EuiCallOut
          color="warning"
          iconType="alert"
          title={
            hasData
              ? translate(`${i18n}.stale.title`, {
                  values: { time: timeOfDay(health.lastSuccessAt as number) },
                  defaultMessage: "Health data from {{time}} - the last refresh failed",
                })
              : translate(`${i18n}.stale.titleNoData`, {
                  defaultMessage: "No health data - PRTG is not reachable",
                })
          }
        >
          <p>
            {health.outage?.status
              ? translate(`${i18n}.stale.bodyStatus`, {
                  values: { status: health.outage.status, retry: WALL_RETRY_SECONDS },
                  defaultMessage:
                    "PRTG not reachable (HTTP {{status}}). Retrying every {{retry}} s.",
                })
              : translate(`${i18n}.stale.body`, {
                  values: { retry: WALL_RETRY_SECONDS },
                  defaultMessage: "PRTG not reachable. Retrying every {{retry}} s.",
                })}
          </p>
        </EuiCallOut>
      )}

      {/* ---- Four counts ---- */}
      {health.loaded && scoped.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
            opacity: stale ? 0.55 : 1,
          }}
        >
          {(["critical", "warning", "healthy", "unknown"] as HealthStatus[]).map(
            (s) => (
              <CountPanel
                key={s}
                status={s}
                label={statusLabel[s]}
                description={statusDescription[s]}
                count={model.counts[s]}
                loading={false}
                large={kiosk}
              />
            ),
          )}
        </div>
      )}

      {body}

      <EuiFlexGroup gutterSize="m" responsive={false} wrap style={noGrow}>
        <EuiFlexItem>
          <EuiText size="xs" color="subdued">
            {translate(`${i18n}.source`, {
              values: { interval: settings.interval },
              defaultMessage:
                "Source: PRTG sensor status, current snapshot · auto-refresh every {{interval}} s",
            })}
          </EuiText>
        </EuiFlexItem>
        {recoveredNames.length > 0 && (
          <EuiFlexItem grow={false}>
            <EuiText size="xs">
              {translate(`${i18n}.recovered`, {
                values: { names: recoveredNames.join(", ") },
                defaultMessage: "Recovered since last refresh: {{names}}",
              })}
            </EuiText>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </div>
  );

  if (kiosk) return content;
  return (
    <EuiPage>
      <EuiPageBody>{content}</EuiPageBody>
    </EuiPage>
  );
};

export default OpsanioHealthWall;
